import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { guestHref } from "@/lib/guest";
import { money, timeAgo } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Area, AreaChart,
} from "recharts";
import {
  IndianRupee, ShoppingBag, Clock, Users, TrendingUp, AlertTriangle, ArrowRight, Trophy,
} from "lucide-react";

const STAT_ICONS = { revenue: IndianRupee, orders: ShoppingBag, active: Clock, customers: Users };

export default function Dashboard() {
  const navigate = useNavigate();
  const { restaurant } = useAuth();
  const slug = restaurant?.slug;
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/reports/dashboard")).data,
    refetchInterval: 15000,
  });

  if (isLoading || !data)
    return <div className="p-8 text-neutral-400">Loading dashboard…</div>;

  const stats = [
    { key: "revenue", label: "Revenue Today", value: money(data.revenue_today), icon: IndianRupee, tint: "var(--kul-primary)" },
    { key: "orders", label: "Orders Today", value: data.orders_today, icon: ShoppingBag, tint: "#9D4EDD" },
    { key: "active", label: "Active Orders", value: data.active_orders, icon: Clock, tint: "var(--kul-warning)" },
    { key: "customers", label: "Total Customers", value: data.total_customers, icon: Users, tint: "var(--kul-success)" },
  ];

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      <PageHeader title="Dashboard" subtitle={`${restaurant?.name || "Your restaurant"} • Avg order ${money(data.avg_order_value)}`}>
        <Button onClick={() => navigate("/pos")} data-testid="dash-new-order"
          className="bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white">
          New Order <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <div key={s.key} className="kul-card p-5 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}
            data-testid={`stat-${s.key}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.1em] text-neutral-500 font-semibold">{s.label}</span>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${s.tint}1a` }}>
                <s.icon className="w-4 h-4" style={{ color: s.tint }} />
              </div>
            </div>
            <div className="mt-3 font-display text-2xl lg:text-3xl font-800 tracking-tight">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 kul-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-700 text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[var(--kul-primary)]" /> 7-Day Sales
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data.sales_trend}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E85D04" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#E85D04" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
              <XAxis dataKey="day" tickLine={false} axisLine={false} style={{ fontSize: 12 }} />
              <YAxis tickLine={false} axisLine={false} style={{ fontSize: 12 }} width={40} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 8, border: "1px solid #eee" }} />
              <Area type="monotone" dataKey="revenue" stroke="#E85D04" strokeWidth={2.5} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="kul-card p-5">
          <h3 className="font-display font-700 text-lg flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-[var(--kul-warning)]" /> Top Sellers
          </h3>
          <div className="space-y-3">
            {data.top_items.map((it, i) => (
              <div key={it.name} className="flex items-center gap-3" data-testid={`top-item-${i}`}>
                <span className="w-6 h-6 rounded-md bg-neutral-100 text-neutral-600 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{it.name}</div>
                  <div className="text-xs text-neutral-400">{it.qty} sold</div>
                </div>
                <span className="text-sm font-mono-k font-semibold">{money(it.revenue)}</span>
              </div>
            ))}
            {!data.top_items.length && <p className="text-sm text-neutral-400">No sales yet.</p>}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 kul-card p-5">
          <h3 className="font-display font-700 text-lg mb-4">Recent Orders</h3>
          <div className="space-y-2">
            {data.recent_orders.map((o) => (
              <div key={o.id} className="flex items-center gap-3 py-2 border-b last:border-0" data-testid={`recent-${o.id}`}>
                <span className="font-mono-k text-sm text-neutral-500">{o.order_number}</span>
                <span className="text-sm flex-1 truncate">{o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</span>
                <Badge variant="outline" className="capitalize text-xs">{o.order_type}</Badge>
                <span className="text-sm font-mono-k font-semibold w-20 text-right">{money(o.total)}</span>
                <span className="text-xs text-neutral-400 w-16 text-right">{timeAgo(o.created_at)}</span>
              </div>
            ))}
            {!data.recent_orders.length && <p className="text-sm text-neutral-400">No orders today yet.</p>}
          </div>
        </div>

        <div className="kul-card p-5">
          <h3 className="font-display font-700 text-lg flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-[var(--kul-danger)]" /> Low Stock
          </h3>
          <div className="space-y-2">
            {data.low_stock.map((s) => (
              <div key={s.id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                <span className="text-sm">{s.name}</span>
                <span className="text-sm font-mono-k text-[var(--kul-danger)]">{s.stock} {s.unit}</span>
              </div>
            ))}
            {!data.low_stock.length && <p className="text-sm text-neutral-400">All stock levels healthy ✓</p>}
          </div>
        </div>
      </div>

      {slug && (
        <div className="kul-card p-5 mt-4">
          <h3 className="font-display font-700 text-lg mb-2">Guest links for this restaurant</h3>
          <p className="text-sm text-neutral-500 mb-3">Share these with diners. Other restaurants cannot see your data.</p>
          <div className="flex flex-wrap gap-3 text-sm">
            <a className="text-[var(--kul-primary)]" href={guestHref(slug, "/order")} target="_blank" rel="noreferrer">Online order</a>
            <a className="text-[var(--kul-primary)]" href={guestHref(slug, "/kiosk")} target="_blank" rel="noreferrer">Kiosk</a>
            <a className="text-[var(--kul-primary)]" href={guestHref(slug, "/tokens")} target="_blank" rel="noreferrer">Token TV</a>
            <a className="text-[var(--kul-primary)]" href={guestHref(slug, "/feedback")} target="_blank" rel="noreferrer">Feedback</a>
          </div>
        </div>
      )}
    </div>
  );
}
